import { styled } from "goober";
import { useCallback, useRef } from "react";
import type { ConnectorWithOAuth } from "../../shared/connectors/getTargetValue";
import { userDisplayName } from "../lib/userDisplayName";
import { ConnectorLogo } from "./ConnectorLogo";
import {
  DropdownRowContent,
  Row,
  RowButton,
  RowLink,
} from "./SearchableDropdown";
import { Icon } from "./Icon";

export type CreatedBy =
  | { name: string; email: string }
  | readonly { name: string; email: string }[]
  | null;

export type AppInfo = ConnectorWithOAuth & { createdBy?: CreatedBy };

export function createdByName(createdBy: CreatedBy | undefined): string | null {
  if (!createdBy) return null;
  if (Array.isArray(createdBy)) {
    const first = createdBy[0];
    return first ? userDisplayName(first) : null;
  }
  return userDisplayName(createdBy as { name: string; email: string });
}

const OptionRow = styled(Row)`
  gap: 0;
  padding: 0;

  & > .row-idle,
  & > .row-hover,
  & > .row-open {
    margin-left: 0;
    margin-right: 4px;
  }
`;

const optionActionStyle = {
  flex: 1,
  minWidth: 0,
  background: "transparent",
} as const;

type Props = {
  name: React.ReactNode;
  secondary?: React.ReactNode;
  trailing?: React.ReactNode;
  size?: number;
  grayscale?: boolean;
} & (
  | { app: ConnectorWithOAuth; template?: never }
  | { app?: never; template: { name: string; identifier: string } }
);

export function ConnectorRow({
  name,
  secondary,
  trailing,
  size = 16,
  grayscale,
  ...logo
}: Props) {
  return (
    <DropdownRowContent
      name={name}
      secondary={secondary}
      trailing={trailing}
      icon={
        <span
          style={{
            display: "flex",
            ...(grayscale ? { filter: "grayscale(100%)", opacity: 0.4 } : {}),
          }}
        >
          {logo.app ? (
            <ConnectorLogo app={logo.app} size={size} />
          ) : logo.template ? (
            <ConnectorLogo template={logo.template} size={size} />
          ) : null}
        </span>
      }
    />
  );
}

// A clickable connector row: navigates to `to`, or runs `onClick`, then closes
// the dropdown. `idle`/`hover` are trailing slots (idle shows at rest, hover on
// row hover) for affordances like a checkmark, open link, or remove button.
// With `openTo` set, holding Shift instead opens that URL (a peek at the
// connector's modal) and, while `shiftHeld`, reveals a hint icon on the right.
export function ConnectorOption({
  app,
  onClick,
  to,
  openTo,
  secondary,
  idle,
  hover,
  shiftHeld,
  close,
}: {
  app: AppInfo;
  onClick?: () => void;
  to?: string;
  openTo?: string;
  secondary?: React.ReactNode;
  idle?: React.ReactNode;
  hover?: React.ReactNode;
  shiftHeld?: boolean;
  close: () => void;
}) {
  const target = shiftHeld && openTo ? openTo : to;
  const actionFocusedRef = useRef(false);
  // Keep the ref stable so cleanup captures focus only when Shift replaces the action element.
  const actionRef = useCallback(
    (element: HTMLButtonElement | HTMLAnchorElement | null) => {
      if (!element) return;
      if (actionFocusedRef.current) element.focus({ preventScroll: true });
      return () => {
        actionFocusedRef.current = document.activeElement === element;
      };
    },
    []
  );
  const content = (
    <ConnectorRow
      app={app}
      name={app.name}
      secondary={secondary ?? createdByName(app.createdBy)}
    />
  );
  return (
    <OptionRow>
      {target ? (
        <RowLink
          ref={actionRef}
          style={optionActionStyle}
          to={target}
          onClick={close}
        >
          {content}
        </RowLink>
      ) : (
        <RowButton
          ref={actionRef}
          style={optionActionStyle}
          onClick={() => {
            close();
            onClick?.();
          }}
        >
          {content}
        </RowButton>
      )}
      {idle && <span className="row-idle">{idle}</span>}
      {hover && <div className="row-hover">{hover}</div>}
      {shiftHeld && openTo && (
        <span className="row-open">
          <Icon name="arrow-up-right" size={12} color="var(--text-secondary)" />
        </span>
      )}
    </OptionRow>
  );
}
